import { forEach } from 'lodash'
import type { Action as TpaAction } from '@wix/data-binding-tpa-actions'
import { prefix as tpaActionPrefix } from '@wix/data-binding-tpa-actions'
import type { ComponentDatabindingApiFactory } from './types'
import { baseComponentBindingApi } from './baseComponentBindingApi'
import { appContext } from '../viewer-app-module/DataBindingAppContext'
import { AppError, DataBindingError } from '../logger'
import type { DynamicPageUrlADT } from '../dataset-controller/rootReducer'
import {
  selectCurrentRecord,
  selectNextDynamicPageUrl,
  selectPreviousDynamicPageUrl,
  getCollectionName,
} from '../dataset-controller/rootReducer'
import { getFieldValue } from '../components/helpers'
import { updateComponentProps } from '../components/helpers/componentValueUtils'
import type {
  Behavior,
  ConnectionConfig,
  ConnectionConfigEventName,
} from '../types'
import type { RecordStoreRecord } from '../record-store/service'
import { isNonEmptyConfig } from '../components/helpers/connectionConfigUtils'

export const defaultComponentBindingApi: ComponentDatabindingApiFactory = (
  component,
  connectionConfig,
  context,
) => {
  const {
    logger,
    platform: { location, utils, executeTpaAction },
  } = appContext
  const { getSchema, getState, getFieldType, wixFormatter, modeIsLivePreview, datasetId } =
    context

  const updateComponent = (previousRecord?: RecordStoreRecord) => {
    updateComponentProps({
      currentRecord: selectCurrentRecord(getState()),
      previousRecord,
      component,
      connectionConfig,
      formatter: wixFormatter,
      modeIsLivePreview,
      getFieldType,
      getSchema,
      logger,
      recordStore: context.recordStore,
      actions: context.actions,
    })
  }

  const getNavigateUrl = (navigate: any, record: RecordStoreRecord) => {
    if (navigate.fieldName) {
      return getFieldValue(record, navigate.fieldName)
    }
    if (navigate.linkObject) {
      return utils.links.toUrl(navigate.linkObject)
    }
  }

  const navigateToDynamicPage = (dynamicPageUrlState: DynamicPageUrlADT) =>
    dynamicPageUrlState.matchWith({
      Empty() { },
      Loading() { },
      Loaded({ url }: any) {
        location.navigateTo(url)
      },
    })

  const bindEvents = (events: ConnectionConfig['events']) => {
    const { datasetApi, getState, actions } = context
    forEach(events, (connectionConfigEvent, eventName) => {
      component.on(eventName as ConnectionConfigEventName, async () => {
        if (!connectionConfigEvent) {
          return
        }
        const { action, postAction } = connectionConfigEvent
        try {
          appContext.lastAction.set({ action, datasetId })

          if (action.startsWith(tpaActionPrefix)) {
            const currentItem = datasetApi.getCurrentItem()
            const collectionId = getCollectionName(getState())
            await executeTpaAction({ currentItem, action: action as TpaAction, collectionId })
            return
          }
          if (action === 'nextDynamicPage') {
            return navigateToDynamicPage(selectNextDynamicPageUrl(getState()))
          }
          if (action === 'previousDynamicPage') {
            return navigateToDynamicPage(
              selectPreviousDynamicPageUrl(getState()),
            )
          }
          if (action === 'applyUserInputFilters') {
            return actions.refresh()
          }
          if (action === 'resetUserFilter') {
            return actions.resetUserInputFilters()
          }

          const record = await Promise.resolve(datasetApi[action]())

          if (postAction && postAction.navigate) {
            const url = getNavigateUrl(postAction.navigate, record)
            location.navigateTo(url)
          }
        } catch (e) {
          // error could have already been logged in datasetApi, so no need to log it again here
          // TODO: but if in some future the internal and external datasetApi's will be split
          // don't forget to properly log errors here.
          if (!(e instanceof DataBindingError)) {
            logger.log(
              new AppError(`${action} operation failed:`, { cause: e }),
            )
          }
        }
      })
    })
  }

  const bindBehaviors = (behaviors: Behavior[]) => {
    const {
      dispatcher,
      eventListeners: { register },
    } = context

    const originalHtml = component.getValue({ propPath: 'html' })
    const originalHtmlHiddenFromAccessibilityTree = `<div aria-hidden="true">${originalHtml}</div>`

    component.setValue(originalHtmlHiddenFromAccessibilityTree, { propPath: 'html' })
    component.setValue('polite', { propPath: 'accessibility.ariaLive' })

    const announcement: {
      state:
      { type: 'untriggered' } |
      { type: 'pendingTriggering'; timerId: ReturnType<typeof setTimeout> } |
      { type: 'triggered' }
    } = { state: { type: 'untriggered' } }

    const revealComponent = () => {
      component.show()
      if (announcement.state.type === 'untriggered') {
        const timerId = setTimeout(() => {
          if (announcement.state.type !== 'pendingTriggering') {
            return
          }
          component.setValue(
            `${originalHtmlHiddenFromAccessibilityTree}<div style="height:0;width:0;overflow:hidden">${originalHtml}</div>`,
            { propPath: 'html' },
          )
          announcement.state = { type: 'triggered' }
        }, 150)
        announcement.state = { type: 'pendingTriggering', timerId }
      }
    }

    const concealComponent = () => {
      component.hide()
      if (announcement.state.type !== 'untriggered') {
        if (announcement.state.type === 'pendingTriggering') {
          clearTimeout(announcement.state.timerId)
        } else {
          component.setValue(originalHtmlHiddenFromAccessibilityTree, { propPath: 'html' })
        }
        announcement.state = { type: 'untriggered' }
      }
    }

    register('beforeSave', concealComponent)
    register('itemValuesChanged', concealComponent)
    behaviors.forEach((behavior: Behavior) => {
      switch (behavior.type) {
        case 'saveSuccessFeedback':
          register('afterSave', revealComponent)
          break
        case 'saveFailureFeedback':
          dispatcher.subscribe('datasetSaveError', revealComponent)
          break
      }
    })
  }

  return {
    ...baseComponentBindingApi(component, connectionConfig, context),
    isValidConnection() {
      return isNonEmptyConfig(connectionConfig)
    },
    bind() {
      const { events, behaviors } = connectionConfig
      if (events) {
        bindEvents(events)
      }
      if (behaviors) {
        bindBehaviors(behaviors)
      }
    },

    clear() {
      const { getFieldType } = context
      const { properties } = connectionConfig
      forEach(properties, (binding, propPath) => {
        const fieldType = getFieldType(binding.fieldName).getOrElse(undefined)
        component.clear({ propPath, fieldType, binding })
      })
    },

    async onRecordsLoaded() {
      const { behaviors } = component.connectionConfig

      updateComponent()

      if (!modeIsLivePreview && behaviors) {
        behaviors.forEach(() => {
          component.hide()
        })
      }
    },

    async onCurrentViewChanged() {
      updateComponent()
    },

    async onCurrentIndexChanged() {
      updateComponent()
    },

    async onCurrentRecordModified(previousRecord) {
      updateComponent(previousRecord)
    },
  }
}
